import { useRouter } from 'next/router'
import { Box, Table, Button, Stack, ScrollArea } from '@mantine/core'
import TitleDivider from './divider'
import { getText } from '../utils/constants'
import useSWR from 'swr'
import { useEffect, useState, useCallback } from 'react'
import Image from 'next/image'

const fetcher = (url) => fetch(url).then((res) => res.json())

function formatDateWithIntl(dateString, locale) {
  const date = new Date(dateString)
  const formatter = new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric'
  })
  return formatter.format(date)
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

const ComingSoonTable = ({ platformId, country, name }) => {
  const router = useRouter()
  const locale = router.locale || 'en-US'
  const t = useCallback((key) => getText('ComingSoon', key), [])

  const { data, error } = useSWR(
    `/api/api_ext/content/coming_soon/${country}?platform_id=${platformId}&limit=10`,
    fetcher
  )

  const [comingsoon, setComingSoon] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [posterStates, setPosterStates] = useState([])

  useEffect(() => {
    if (data) {
      if (data.length === 0) {
        setIsLoading(false)
        return
      }
      setComingSoon(data.slice(0, 5))
      setPosterStates(
        data.slice(0, 5).map((item) => ({
          src: item.poster || '/noimage.png',
          isFallback: !item.poster
        }))
      )
      setIsLoading(false)
    }
    if (error) {
      console.error(t('loadingError'), error)
      setIsLoading(false)
    }
  }, [data, error, t])

  const handlePosterError = (index) => {
    setPosterStates((prevState) => {
      const newState = [...prevState]
      newState[index] = { src: '/noimage.png', isFallback: true }
      return newState
    })
  }

  const headerStyle = {
    backgroundColor: '#0E1922',
    color: '#FFFFFF',
    boxShadow: '0px -1px 0px 0px rgba(123,57,182,1) inset',
    borderBottom: 'none'
  }

  if (isLoading || !data || data.length === 0) {
    return null
  }

  return (
    <>
      <Box mt={20}>
        <TitleDivider titleText={t('comingSoon') + name.toUpperCase()} />
      </Box>

      <Stack
        align="center"
        spacing={0}
        sx={(theme) => ({
          boxShadow: '0px -2px 0px 0px rgba(123,57,182,1) inset',
          backgroundColor: '#121E274D',
          padding: '20px',
          borderRadius: '10px',
          width: '100%',
          margin: '0 auto',
          [theme.fn.smallerThan('sm')]: {
            padding: '10px'
          }
        })}
      >
        <Box sx={{ width: '100%' }}>
          <ScrollArea>
            <Table
              sx={(theme) => ({
                borderRadius: '10px',
                overflow: 'hidden',
                marginTop: '10px',
                minWidth: '600px',
                [theme.fn.smallerThan('sm')]: {
                  fontSize: theme.fontSizes.xs
                }
              })}
            >
              <thead>
                <tr>
                  <th style={headerStyle}>{t('poster')}</th>
                  <th style={headerStyle}>{t('title')}</th>
                  <th style={headerStyle}>{t('type')}</th>
                  <th style={headerStyle}>{t('availabilityDay')}</th>
                </tr>
              </thead>
              <tbody>
                {comingsoon.map((item, index) => (
                  <tr
                    key={item.uid}
                    style={{
                      filter: `blur(${Math.max(0, index - 1)}px)`,
                      transition: 'filter 0.3s ease',
                      borderStyle: 'hidden'
                    }}
                  >
                    <td
                      style={{
                        padding: '7px 10px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        width: '100px'
                      }}
                    >
                      <Box
                        sx={{
                          width: '45px',
                          height: '67px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          backgroundColor: posterStates[index]?.isFallback
                            ? 'rgba(255,255,255,0.1)'
                            : 'transparent'
                        }}
                      >
                        <Image
                          src={posterStates[index]?.src}
                          onError={() => handlePosterError(index)}
                          width={posterStates[index]?.isFallback ? 30 : 45}
                          height={posterStates[index]?.isFallback ? 30 : 67}
                          alt="poster"
                          style={{
                            borderRadius: '4px',
                            objectFit: posterStates[index]?.isFallback
                              ? 'contain'
                              : 'cover'
                          }}
                        />
                      </Box>
                    </td>
                    <td style={{ color: '#FFFFFF' }}>{item.title}</td>
                    <td style={{ color: '#FFFFFF' }}>
                      {capitalizeFirstLetter(item.type)}
                    </td>
                    <td style={{ color: '#FFFFFF' }}>
                      {formatDateWithIntl(item.availability_day, locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
        </Box>

        <Button
          radius="sm"
          size="xs"
          sx={(theme) => ({
            marginTop: '20px',
            backgroundColor: '#7B39B6',
            color: '#FFFFFF',
            [theme.fn.smallerThan('sm')]: {
              width: '100%'
            }
          })}
        >
          {t('getFullComingSoonList') + name.toUpperCase()}
        </Button>
      </Stack>
    </>
  )
}

export default ComingSoonTable
